import { METROPOLITAN_FRANCE_IATA_CODES } from '@massilia/queries'
import { differenceInMonths } from 'date-fns'
import { Phone } from 'lucide-react'

import type { FlightSearchSchemaType } from '@/lib/schemas'

const MADAGASCAR_IATA_CODES = new Set([
  'DIE',
  'FTU',
  'MJN',
  'MOQ',
  'SMS',
  'SVB',
  'TLE',
  'TMM',
  'WMN',
])

interface ResultsBannersProps {
  flightSearchData: FlightSearchSchemaType
}

export function ResultsBanners({ flightSearchData }: ResultsBannersProps) {
  const banners: { key: string; message: React.ReactNode }[] = []

  // Banner 1: Corsair for DZA/TNR multi-destination
  if (flightSearchData.type === 'multi-destination') {
    const hasDzaOrTnr = flightSearchData.flight.some(
      (leg) =>
        leg.departure.value === 'DZA' ||
        leg.departure.value === 'TNR' ||
        leg.destination.value === 'DZA' ||
        leg.destination.value === 'TNR'
    )
    if (hasDzaOrTnr) {
      banners.push({
        key: 'corsair',
        message:
          'Pour les vols Multidestinations de/vers Mayotte et Antananarivo, vous pouvez nous appeler au 0491910698 afin d\u2019obtenir les tarifs de la compagnie Corsair.',
      })
    }
  }

  // Banner 2: One-way non France→France or trip > 3 months
  const isOneWayNonFrance = (() => {
    if (flightSearchData.type !== 'one-way') return false
    const dep = flightSearchData.flight[0]?.departure.value
    const dest = flightSearchData.flight[0]?.destination.value
    if (!dep || !dest) return false
    const bothFrance =
      METROPOLITAN_FRANCE_IATA_CODES.has(dep) && METROPOLITAN_FRANCE_IATA_CODES.has(dest)
    return !bothFrance
  })()

  const isTripOver3Months = (() => {
    const flights = flightSearchData.flight
    const firstDate = flights[0]?.date.from
    const lastDate =
      flightSearchData.type === 'round-trip' ? flights[0]?.date.to : flights.at(-1)?.date.from
    if (!firstDate || !lastDate) return false
    return differenceInMonths(lastDate, firstDate) >= 3
  })()

  if (isOneWayNonFrance || isTripOver3Months) {
    banners.push({
      key: 'traveldoc',
      message: (
        <>
          Pour ce type de voyage, merci de vérifier les conditions d&apos;entrée à destination
          (visa, validité du passeport...){' '}
          <a
            className="font-semibold underline"
            href="https://www.traveldoc.aero"
            rel="noopener noreferrer"
            target="_blank"
          >
            ici
          </a>
        </>
      ),
    })
  }

  // Banner 3: Madagascar destinations
  const hasMadagascarDestination = flightSearchData.flight.some((leg) =>
    MADAGASCAR_IATA_CODES.has(leg.destination.value)
  )
  if (hasMadagascarDestination) {
    banners.push({
      key: 'madagascar',
      message:
        'Pour cette destination à Madagascar, si vous ne trouvez pas l\u2019offre qui vous convient, merci de nous appeler au 0491910698',
    })
  }

  if (banners.length === 0) return null

  return (
    <div className="mb-4 flex flex-col gap-2">
      {banners.map((banner) => (
        <div
          className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3"
          key={banner.key}
        >
          <Phone className="mt-0.5 size-4 shrink-0 text-amber-600" />
          <p className="text-sm text-amber-800">{banner.message}</p>
        </div>
      ))}
    </div>
  )
}
